/**
 * QuickActionsFAB - Floating Action Button for quick actions (Desktop only)
 *
 * A floating + button at the bottom-left corner that opens a beautiful
 * radial menu of action buttons with smooth stagger animation.
 *
 * Shows 6 actions:
 * - New appointment
 * - New patient
 * - Treatment summary
 * - Add reminder
 * - New lead
 * - Create invoice (only if invoice system is integrated)
 */

import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectClinicProfile,
  selectCanCreateGreenInvoice,
  selectHasPermission,
  selectIsAdmin,
} from "../../store/slices/authSlice";
import {
  Plus,
  X,
  CalendarPlus,
  UserPlus,
  NotePencil,
  Bell,
  UserCirclePlus,
  Receipt,
} from "@phosphor-icons/react";
import "./QuickActionsFAB.css";

function QuickActionsFAB({ onNewAppointment, onNewInvoice }) {
  const navigate = useNavigate();
  const clinicProfile = useSelector(selectClinicProfile);
  const isAdminUser = useSelector(selectIsAdmin);
  const canManageLeadsToggle = useSelector(selectHasPermission("leads.access"));
  const canAccessLeads = isAdminUser || canManageLeadsToggle; // Leads v2: clinic toggle, admins always
  const [isOpen, setIsOpen] = useState(false);
  const fabRef = useRef(null);

  const isUsingGreenInvoice = useSelector(selectCanCreateGreenInvoice);

  // Define all actions
  const baseActions = [
    {
      id: "new-appointment",
      label: "פגישה חדשה",
      icon: CalendarPlus,
      onClick: () => {
        onNewAppointment?.();
        setIsOpen(false);
      },
    },
    {
      id: "new-patient",
      label: "מטופל חדש",
      icon: UserPlus,
      onClick: () => {
        navigate("/dashboard/patients/new");
        setIsOpen(false);
      },
    },
    {
      id: "treatment-summary",
      label: "סיכום פגישה",
      icon: NotePencil,
      onClick: () => {
        navigate("/dashboard/patients");
        setIsOpen(false);
      },
    },
    {
      id: "add-reminder",
      label: "תזכורת חדשה",
      icon: Bell,
      onClick: () => {
        navigate("/dashboard/tasks");
        setIsOpen(false);
      },
    },
    ...(canAccessLeads
      ? [
          {
            id: "new-lead",
            label: "מתעניין חדש",
            icon: UserCirclePlus,
            onClick: () => {
              navigate("/dashboard/tasks?tab=leads&action=new");
              setIsOpen(false);
            },
          },
        ]
      : []),
  ];

  const actions = isUsingGreenInvoice
    ? [
        ...baseActions,
        {
          id: "create-invoice",
          label: "הנפקת חשבונית",
          icon: Receipt,
          onClick: () => {
            onNewInvoice?.();
            setIsOpen(false);
          },
        },
      ]
    : baseActions;

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fabRef.current && !fabRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const toggleFAB = () => {
    setIsOpen(!isOpen);
  };

  // Calculate position for each action button
  // Spread them vertically with increasing X offset for a nice arc effect
  const getActionStyle = (index, total) => {
    // Vertical spacing between buttons
    const verticalGap = 65;
    // Starting Y offset from the FAB
    const baseY = 80;
    // X offset increases as we go up (creates arc effect)
    const baseX = 70;
    const xIncrement = 35;

    const y = baseY + (index * verticalGap);
    const x = baseX + (index * xIncrement);

    return {
      "--action-x": `${x}px`,
      "--action-y": `${-y}px`,
      "--delay": `${index * 50}ms`,
      "--delay-close": `${(total - 1 - index) * 35}ms`,
    };
  };

  return (
    <div className={`quick-actions-fab ${isOpen ? "open" : ""}`} ref={fabRef}>
      {/* Backdrop overlay */}
      {isOpen && (
        <div className="fab-overlay" onClick={() => setIsOpen(false)} />
      )}

      {/* Action buttons */}
      <div className="fab-actions-container">
        {actions.map((action, index) => {
          const style = getActionStyle(index, actions.length);
          const ActionIcon = action.icon;

          return (
            <button
              key={action.id}
              className={`fab-action-item ${isOpen ? "visible" : ""}`}
              style={style}
              onClick={action.onClick}
              aria-label={action.label}
            >
              <span className="fab-action-icon">
                <ActionIcon size={22} weight="bold" />
              </span>
              <span className="fab-action-text">{action.label}</span>
            </button>
          );
        })}
      </div>

      {/* Main FAB button */}
      <button
        className="fab-main-button"
        onClick={toggleFAB}
        aria-label={isOpen ? "סגור תפריט פעולות" : "פתח תפריט פעולות מהירות"}
        aria-expanded={isOpen}
      >
        <span className="fab-main-icon">
          {isOpen ? (
            <X size={26} weight="bold" />
          ) : (
            <Plus size={26} weight="bold" />
          )}
        </span>
      </button>
    </div>
  );
}

export default QuickActionsFAB;
